import { NumericMatrixMap } from '@/types/powerMarkets/SeriesData.ts';
import { compareDdMmYyyyDateStrings } from '@/utils/dateUtils.ts';

/**
 * Collect x values already present in historic SQL data and realtime day buckets.
 * Optionally exclude one day key (the bucket being written).
 */
export function collectExistingSeriesXs (
    historicPoints: Array<Array<number>> | null | undefined,
    realtimeByDay: NumericMatrixMap | null | undefined,
    excludeDay?: string,
): Set<number> {
    const existingXs = new Set<number>();

    if (historicPoints) {
        historicPoints.forEach((point) => {
            if (point != null && typeof point[0] === 'number') {
                existingXs.add(point[0]);
            }
        });
    }

    if (realtimeByDay) {
        Object.keys(realtimeByDay).forEach((day) => {
            if (excludeDay != null && day === excludeDay) return;
            const dayPoints = realtimeByDay[day];
            if (!dayPoints) return;
            dayPoints.forEach((point) => {
                if (point != null && typeof point[0] === 'number') {
                    existingXs.add(point[0]);
                }
            });
        });
    }

    return existingXs;
}

/**
 * Keep points whose x is not already present (existing data prevails).
 */
export function filterPointsNotInExistingXs (
    incoming: Array<Array<number>> | null | undefined,
    existingXs: Set<number>,
): Array<Array<number>> {
    if (!incoming || incoming.length === 0) return [];
    if (existingXs.size === 0) return incoming;

    return incoming.filter((point) => {
        if (point == null || typeof point[0] !== 'number') return false;
        return !existingXs.has(point[0]);
    });
}

/**
 * Write SQL historic points when the payload is from HistoricSeriesApi
 * or when no historic data is stored yet for this series.
 */
export function writeHistoricSqlIfAllowed (
    historicBySeriesId: NumericMatrixMap,
    seriesId: string,
    sqlPoints: Array<Array<number>> | null | undefined,
    isHistoricApi: boolean,
): void {
    if (sqlPoints == null) return;
    if (!isHistoricApi && historicBySeriesId[seriesId]?.length) return;
    historicBySeriesId[seriesId] = sqlPoints;
}

/**
 * Merge realtime day buckets in chronological order.
 * Raw payloads are preserved separately; filtered buckets are rebuilt from raw
 * so points dropped for historic/earlier-day overlap can be restored later.
 */
export function mergeRealtimeDayBuckets (
    historicPoints: Array<Array<number>> | null | undefined,
    filteredRealtimeByDay: NumericMatrixMap,
    rawRealtimeByDay: NumericMatrixMap,
    incomingByDay: NumericMatrixMap = {},
): void {
    const orderedDays = [...new Set([
        ...Object.keys(rawRealtimeByDay),
        ...Object.keys(incomingByDay),
    ])]
        .filter(day => day !== 'SQL')
        .sort(compareDdMmYyyyDateStrings);

    const existingXs = collectExistingSeriesXs(historicPoints, null);

    orderedDays.forEach((day) => {
        const rawSource = incomingByDay[day] ?? rawRealtimeByDay[day] ?? [];
        rawRealtimeByDay[day] = rawSource;
        const filtered = filterPointsNotInExistingXs(rawSource, existingXs);
        filteredRealtimeByDay[day] = filtered;
        filtered.forEach((point) => {
            if (point != null && typeof point[0] === 'number') {
                existingXs.add(point[0]);
            }
        });
    });
}
